/**
 * Stylized, illustrative preview of the authenticated dashboard.
 *
 * There's no real screenshot to show on the public landing page (and this
 * app has no screenshot-generation pipeline), so this renders a lightweight,
 * clearly-illustrative "browser frame" mockup built from plain MUI
 * primitives — no fabricated data is presented as if it were live; it's a
 * stand-in for "Professional dashboard mockup" / "Platform Preview".
 *
 * `variant` picks which mini-panel renders inside the frame so the same
 * component can serve both the Hero's "dashboard" preview and the four
 * Platform Preview cards (scanner / options / portfolio / algo).
 */

import type { ReactElement } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";

export type MockupVariant = "dashboard" | "scanner" | "options" | "portfolio" | "algo";

interface PlatformMockupProps {
  variant: MockupVariant;
  compact?: boolean;
}

const DOT_COLORS = ["#F87171", "#FBBF24", "#34D399"];

function TitleBar({ label }: { label: string }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 2, py: 1, bgcolor: "#0B1424", borderBottom: "1px solid #1E293B" }}
    >
      {DOT_COLORS.map((color) => (
        <Box key={color} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      ))}
      <Typography variant="caption" sx={{ color: "#64748B", ml: 1.5 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function StatChip({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <Box sx={{ bgcolor: "#111C33", border: "1px solid #1E293B", borderRadius: 1.5, p: 1.25, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color: positive === undefined ? "#fff" : positive ? "#34D399" : "#F87171" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function Row({ left, mid, right, rightColor }: { left: string; mid?: string; right: string; rightColor: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ color: "#CBD5E1" }}>
        {left}
      </Typography>
      {mid && (
        <Typography
          variant="caption"
          sx={{ color: "#94A3B8", bgcolor: "#1E293B", px: 1, borderRadius: 999, fontSize: "0.65rem" }}
        >
          {mid}
        </Typography>
      )}
      <Typography variant="caption" fontWeight={700} sx={{ color: rightColor }}>
        {right}
      </Typography>
    </Stack>
  );
}

function DashboardBody() {
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.25} mb={1.5}>
        <StatChip label="Portfolio Value" value="Populated after login" />
        <StatChip label="Today's P&L" value="Populated after login" />
        <StatChip label="Broker Status" value="Connected" positive />
      </Stack>
      <Box sx={{ bgcolor: "#111C33", border: "1px solid #1E293B", borderRadius: 1.5, p: 1.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: "#CBD5E1", display: "block", mb: 0.5 }}>
          Market Scanner
        </Typography>
        <Row left="NIFTY 50" right="Tracked" rightColor="#94A3B8" />
        <Row left="BANK NIFTY" right="Tracked" rightColor="#94A3B8" />
        <Row left="Your Watchlist" right="Configured by you" rightColor="#94A3B8" />
      </Box>
    </Box>
  );
}

function ScannerBody() {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="caption" fontWeight={700} sx={{ color: "#CBD5E1", display: "block", mb: 1 }}>
        AI Market Scanner
      </Typography>
      <Row left="NIFTY 50" mid="Tracked" right="—" rightColor="#94A3B8" />
      <Row left="BANK NIFTY" mid="Tracked" right="—" rightColor="#94A3B8" />
      <Row left="Your Watchlist" mid="Configured by you" right="—" rightColor="#94A3B8" />
    </Box>
  );
}

function OptionsBody() {
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.25} mb={1.5}>
        <StatChip label="OI Score" value="Live after login" />
        <StatChip label="PCR" value="Live after login" />
        <StatChip label="India VIX" value="Live after login" />
      </Stack>
      <Row left="Max Pain" right="Calculated from your data" rightColor="#94A3B8" />
      <Row left="Support" right="Calculated from your data" rightColor="#94A3B8" />
      <Row left="Resistance" right="Calculated from your data" rightColor="#94A3B8" />
    </Box>
  );
}

function PortfolioBody() {
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.25} mb={1.5}>
        <StatChip label="Invested" value="Populated after login" />
        <StatChip label="Current" value="Populated after login" />
        <StatChip label="Returns" value="Populated after login" />
      </Stack>
      <Row left="Your Holdings" right="Synced from broker" rightColor="#94A3B8" />
      <Row left="Allocation" right="Synced from broker" rightColor="#94A3B8" />
      <Row left="Performance" right="Synced from broker" rightColor="#94A3B8" />
    </Box>
  );
}

function AlgoBody() {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ bgcolor: "#111C33", border: "1px solid #1E293B", borderRadius: 1.5, p: 1.25, mb: 1 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: "#CBD5E1" }}>
          Momentum Breakout
        </Typography>
        <Typography variant="caption" sx={{ color: "#94A3B8", display: "block" }}>
          Backtest and deploy after you sign in
        </Typography>
      </Box>
      <Box sx={{ bgcolor: "#111C33", border: "1px solid #1E293B", borderRadius: 1.5, p: 1.25, mb: 1 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: "#CBD5E1" }}>
          OI Range Reversal
        </Typography>
        <Typography variant="caption" sx={{ color: "#94A3B8", display: "block" }}>
          Backtest and deploy after you sign in
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: "#64748B" }}>
        Live performance appears here once a strategy is running.
      </Typography>
    </Box>
  );
}

const VARIANT_CONFIG: Record<MockupVariant, { label: string; body: () => ReactElement }> = {
  dashboard: { label: "app.tekstarinvest.com/dashboard", body: DashboardBody },
  scanner: { label: "app.tekstarinvest.com/stocks", body: ScannerBody },
  options: { label: "app.tekstarinvest.com/options-dashboard", body: OptionsBody },
  portfolio: { label: "app.tekstarinvest.com/portfolio", body: PortfolioBody },
  algo: { label: "app.tekstarinvest.com/strategies", body: AlgoBody },
};

export function PlatformMockup({ variant, compact = false }: PlatformMockupProps) {
  const { label, body: Body } = VARIANT_CONFIG[variant];

  return (
    <Paper
      elevation={compact ? 2 : 8}
      sx={{
        bgcolor: "#0F172A",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid #1E293B",
        width: "100%",
      }}
    >
      <TitleBar label={label} />
      <Body />
    </Paper>
  );
}

export default PlatformMockup;
